import { defineComponent, ref } from "vue";
import Utils, { UserError } from "../utils/Utils";
import OfferLoader from "./OfferLoader";
import PhotoUploader from "./PhotoUploader";

export class UploadError extends UserError {
    public readonly soft: boolean;

    constructor(message: string, soft: boolean) {
        super(message);

        this.soft = soft;
    }
}

export interface UploadFile {
    id: string;

    name: string;
    file: File;
    blob?: Blob;

    photo: boolean;
}

const InvoiceUploaderFiles = defineComponent({
    template: `
        <div class="InvoiceUploaderFiles">
            <div class="InvoiceUploaderFiles__inner">
                <div class="InvoiceUploaderFiles__items">
                    <div v-for="file in files" class="InvoiceUploaderFiles__item">
                        <div class="InvoiceUploaderFiles__file">
                            <div class="InvoiceUploaderFiles__name">{{ file.name }}</div>
                            <div class="InvoiceUploaderFiles__size">{{ size(file) }}</div>
                        </div>

                        <button type="type" @click="$emit('remove', file)" class="InvoiceUploaderFiles__remove" :disabled="loading">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                                <path d="M13.8869 4.05335H11.1402V3.26668C11.1402 2.25335 10.3202 1.43335 9.30691 1.43335H6.68691C5.67358 1.43335 4.85358 2.25335 4.85358 3.26668V4.05335H2.11358C1.82691 4.05335 1.58691 4.28668 1.58691 4.58002C1.58691 4.87335 1.82025 5.10668 2.11358 5.10668H13.8936C14.1802 5.10668 14.4202 4.87335 14.4202 4.58002C14.4202 4.28668 14.1869 4.05335 13.8936 4.05335H13.8869ZM5.90691 3.26668C5.90691 2.83335 6.26025 2.48002 6.69358 2.48002H9.31358C9.74691 2.48002 10.1002 2.83335 10.1002 3.26668V4.05335H5.91358V3.26668H5.90691Z" fill="#005537"/>
                                <path d="M12.6197 5.81323C12.333 5.79323 12.0797 6.00656 12.0597 6.29323L11.493 13.7932C11.4597 14.1999 11.1197 14.5199 10.713 14.5199H5.28632C4.87965 14.5199 4.53299 14.1999 4.50632 13.7932L3.93965 6.29323C3.91965 6.00656 3.66632 5.78656 3.37965 5.81323C3.09299 5.83323 2.87299 6.08656 2.89965 6.37323L3.46632 13.8732C3.53965 14.8266 4.33965 15.5666 5.29299 15.5666H10.7197C11.673 15.5666 12.473 14.8199 12.5463 13.8732L13.113 6.37323C13.133 6.08656 12.9197 5.83323 12.633 5.81323H12.6197Z" fill="#005537"/>
                                <path d="M6.6937 12.2934C6.6937 12.2934 6.7137 12.2934 6.72703 12.2934C7.0137 12.2801 7.2337 12.0267 7.22037 11.7401L7.00037 7.81341C6.98703 7.52674 6.74037 7.30008 6.44703 7.32008C6.16037 7.33341 5.94037 7.58674 5.9537 7.87341L6.1737 11.8001C6.18703 12.0801 6.42037 12.2934 6.6937 12.2934Z" fill="#005537"/>
                                <path d="M9.30654 12.2934C9.57987 12.2934 9.81321 12.0801 9.82654 11.8001L10.0465 7.87341C10.0599 7.58674 9.83987 7.34008 9.55321 7.32008C9.26654 7.30008 9.01987 7.52674 8.99987 7.81341L8.77987 11.7401C8.76654 12.0267 8.98654 12.2734 9.27321 12.2934C9.28654 12.2934 9.29321 12.2934 9.30654 12.2934Z" fill="#005537"/>
                            </svg>
                        </button>
                    </div>
                </div>

                <div class="InvoiceUploaderFiles__footer">
                    <div v-if="error" class="InvoiceUploaderFiles__error" v-html="error"></div>

                    <div class="InvoiceUploaderFiles__controls">
                        <button type="type" @click="$emit('add')" :disabled="loading" class="InvoiceUploaderFiles__button">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                                <path d="M4.53 6.12943C4.66333 6.12943 4.79667 6.07609 4.89667 5.97609L7.62333 3.24943V11.4561C7.62333 11.7428 7.85667 11.9694 8.13667 11.9694C8.41667 11.9694 8.65 11.7361 8.65 11.4561V3.25609L11.3767 5.98276C11.5767 6.18276 11.9033 6.18276 12.11 5.98276C12.31 5.78276 12.31 5.45609 12.11 5.24943L8.50333 1.64276C8.50333 1.64276 8.45 1.59609 8.42333 1.57609C8.41667 1.57609 8.41 1.56943 8.39667 1.56276C8.37667 1.54943 8.35667 1.53609 8.33 1.52943C8.31667 1.52943 8.31 1.52943 8.29667 1.51609C8.27667 1.50943 8.25667 1.50276 8.23 1.49609C8.21667 1.49609 8.20333 1.49609 8.18333 1.49609C8.16333 1.49609 8.14333 1.49609 8.13 1.49609C8.11667 1.49609 8.09 1.49609 8.07667 1.49609C8.06333 1.49609 8.05 1.49609 8.03 1.49609C8.01 1.49609 7.99 1.50943 7.96333 1.51609C7.95 1.51609 7.94333 1.51609 7.93 1.52276C7.91 1.52943 7.88333 1.54276 7.86333 1.55609C7.85667 1.55609 7.84333 1.56276 7.83667 1.56943C7.81 1.58943 7.78333 1.60943 7.75667 1.63609L4.15 5.24276C3.95 5.44276 3.95 5.76943 4.15 5.97609C4.25 6.07609 4.38333 6.12943 4.51667 6.12943H4.53Z" fill="#282828"/>
                                <path d="M14.7663 8.20288C14.4797 8.20288 14.253 8.43621 14.253 8.71621C14.253 11.9962 11.5863 14.6629 8.30634 14.6629C5.02634 14.6629 2.35967 11.9962 2.35967 8.71621C2.35967 8.42955 2.12634 8.20288 1.84634 8.20288C1.56634 8.20288 1.33301 8.43621 1.33301 8.71621C1.33301 12.5629 4.46634 15.6962 8.31301 15.6962C12.1597 15.6962 15.293 12.5629 15.293 8.71621C15.293 8.42955 15.0597 8.20288 14.7797 8.20288H14.7663Z" fill="#282828"/>
                                </svg>

                            Dodaj kolejny plik
                        </button>

                        <button type="type" @click="$emit('removeall')" :disabled="loading" class="InvoiceUploaderFiles__button">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                                <path d="M13.8869 4.08289H11.1402V3.29622C11.1402 2.28289 10.3202 1.46289 9.30691 1.46289H6.68691C5.67358 1.46289 4.85358 2.28289 4.85358 3.29622V4.08289H2.11358C1.82691 4.08289 1.58691 4.31622 1.58691 4.60956C1.58691 4.90289 1.82025 5.13622 2.11358 5.13622H13.8936C14.1802 5.13622 14.4202 4.90289 14.4202 4.60956C14.4202 4.31622 14.1869 4.08289 13.8936 4.08289H13.8869ZM5.90691 3.29622C5.90691 2.86289 6.26025 2.50956 6.69358 2.50956H9.31358C9.74691 2.50956 10.1002 2.86289 10.1002 3.29622V4.08289H5.91358V3.29622H5.90691Z" fill="#282828"/>
                                <path d="M12.6197 5.84301C12.333 5.82301 12.0797 6.03634 12.0597 6.32301L11.493 13.823C11.4597 14.2297 11.1197 14.5497 10.713 14.5497H5.28632C4.87965 14.5497 4.53299 14.2297 4.50632 13.823L3.93965 6.32301C3.91965 6.03634 3.66632 5.81635 3.37965 5.84301C3.09299 5.86301 2.87299 6.11635 2.89965 6.40301L3.46632 13.903C3.53965 14.8563 4.33965 15.5963 5.29299 15.5963H10.7197C11.673 15.5963 12.473 14.8497 12.5463 13.903L13.113 6.40301C13.133 6.11635 12.9197 5.86301 12.633 5.84301H12.6197Z" fill="#282828"/>
                                <path d="M6.6937 12.323C6.6937 12.323 6.7137 12.323 6.72703 12.323C7.0137 12.3096 7.2337 12.0563 7.22037 11.7696L7.00037 7.84295C6.98703 7.55628 6.74037 7.32962 6.44703 7.34962C6.16037 7.36295 5.94037 7.61628 5.9537 7.90295L6.1737 11.8296C6.18703 12.1096 6.42037 12.323 6.6937 12.323Z" fill="#282828"/>
                                <path d="M9.30654 12.323C9.57987 12.323 9.81321 12.1096 9.82654 11.8296L10.0465 7.90295C10.0599 7.61628 9.83987 7.36962 9.55321 7.34962C9.26654 7.32962 9.01987 7.55628 8.99987 7.84295L8.77987 11.7696C8.76654 12.0563 8.98654 12.303 9.27321 12.323C9.28654 12.323 9.29321 12.323 9.30654 12.323Z" fill="#282828"/>
                            </svg>

                            Wyczyść listę
                        </button>
                    </div>

                    <slot></slot>
                </div>
            </div>
        </div>
    `,

    emits: [
        'add',
        'remove',
    ],

    props: {
        files: { type: Array, required: true },

        loading: { type: Boolean },

        error: { type: String },
    },

    methods: {
        size(file: UploadFile) {
            return Math.round(file.file.size / 1000) + ' KB';
        },
    },
});

let globalfiles = ref([]);

export default defineComponent({
    components: {
        OfferLoader,
        PhotoUploader,
        InvoiceUploaderFiles,
    },

    template: `
        <div class="InvoiceUploader" :class="{ 'InvoiceUploader--over': over, 'InvoiceUploader--loading': loading }">
            <input ref="upload" type="file" @change="onChange" :required="true" multiple accept="application/pdf,image/jpeg,image/png,image/heic,.heic">

            <InvoiceUploaderFiles
                v-if="files.length"

                :files="files"
                :loading="loading"
                :error="error"

                @add="onManagerAdd"
                @remove="onPhotoRemove"
                @removeall="onPhotoRemoveAll"
            >
                <slot name="uploadedfooter"></slot>
            </InvoiceUploaderFiles>


            <div v-if="!files.length" class="InvoiceUploader__buttons">
                <button type="button" class="InvoiceUploader__button"
                    @click="onClickUpload"

                    @drop="onDrop"
                    @dragenter="onDragEnter"
                    @dragover="onDragOver"
                    @dragleave="onDragEnd"

                    :disabled="disabled || loading"
                >
                    <div>
                        <div class="InvoiceUploader__icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="49" viewBox="0 0 48 49" fill="none">
                                <path d="M13.59 17.3C13.99 17.3 14.39 17.14 14.69 16.84L22.87 8.66002V33.28C22.87 34.14 23.57 34.82 24.41 34.82C25.25 34.82 25.95 34.12 25.95 33.28V8.68002L34.13 16.86C34.73 17.46 35.71 17.46 36.33 16.86C36.93 16.26 36.93 15.28 36.33 14.66L25.51 3.84002C25.51 3.84002 25.35 3.70002 25.27 3.64002C25.25 3.64002 25.23 3.62002 25.19 3.60002C25.13 3.56002 25.07 3.52002 24.99 3.50002C24.95 3.50002 24.93 3.50002 24.89 3.46002C24.83 3.44002 24.77 3.42002 24.69 3.40002C24.65 3.40002 24.61 3.40002 24.55 3.40002C24.49 3.40002 24.43 3.40002 24.39 3.40002C24.35 3.40002 24.27 3.40002 24.23 3.40002C24.19 3.40002 24.15 3.40002 24.09 3.40002C24.03 3.40002 23.97 3.44002 23.89 3.46002C23.85 3.46002 23.83 3.46002 23.79 3.48002C23.73 3.50002 23.65 3.54002 23.59 3.58002C23.57 3.58002 23.53 3.60002 23.51 3.62002C23.43 3.68002 23.35 3.74002 23.27 3.82002L12.45 14.64C11.85 15.24 11.85 16.22 12.45 16.84C12.75 17.14 13.15 17.3 13.55 17.3H13.59Z" fill="currentColor" />
                                <path d="M44.3 23.52C43.44 23.52 42.76 24.22 42.76 25.06C42.76 34.9 34.76 42.9 24.92 42.9C15.08 42.9 7.08 34.9 7.08 25.06C7.08 24.2 6.38 23.52 5.54 23.52C4.7 23.52 4 24.22 4 25.06C4 36.6 13.4 46 24.94 46C36.48 46 45.88 36.6 45.88 25.06C45.88 24.2 45.18 23.52 44.34 23.52H44.3Z" fill="currentColor"/>
                            </svg>
                        </div>

                        <div v-if="mobile" class="InvoiceUploader__title">{{ label_upload_mobile }}</div>
                        <div v-if="!mobile" class="InvoiceUploader__title">Przeciągnij i upuść plik tutaj, aby go przesłać<br>lub</div>

                        <div v-if="!disabled && !mobile" class="button button--master">Wgraj pliki</div>
                        <div v-if="disabled && !mobile" class="button button--master" disabled>Wgraj pliki</div>

                        <div v-if="mobile" class="InvoiceUploader__formats">Obsługujemy pliki PDF</div>
                        <div v-if="!mobile" class="InvoiceUploader__formats">Wspierany format plików to PDF</div>
                    </div>
                </button>

                <button v-if="false && mobile" type="button" class="InvoiceUploader__photo" @click="onPhotoOpen">
                    <div>
                        <div class="InvoiceUploader__icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="41" height="40" viewBox="0 0 41 40" fill="none">
                                <g clip-path="url(#clip0_5_6435)">
                                    <path d="M20.5 33.3337H8.83333C7.94928 33.3337 7.10143 32.9825 6.47631 32.3573C5.85119 31.7322 5.5 30.8844 5.5 30.0003V15.0003C5.5 14.1163 5.85119 13.2684 6.47631 12.6433C7.10143 12.0182 7.94928 11.667 8.83333 11.667H10.5C11.3841 11.667 12.2319 11.3158 12.857 10.6907C13.4821 10.0656 13.8333 9.21771 13.8333 8.33366C13.8333 7.89163 14.0089 7.46771 14.3215 7.15515C14.634 6.84259 15.058 6.66699 15.5 6.66699H25.5C25.942 6.66699 26.3659 6.84259 26.6785 7.15515C26.9911 7.46771 27.1667 7.89163 27.1667 8.33366C27.1667 9.21771 27.5179 10.0656 28.143 10.6907C28.7681 11.3158 29.6159 11.667 30.5 11.667H32.1667C33.0507 11.667 33.8986 12.0182 34.5237 12.6433C35.1488 13.2684 35.5 14.1163 35.5 15.0003V20.8337" stroke="#5A00B9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M27.166 31.667H37.166" stroke="#5A00B9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M32.166 26.667V36.667" stroke="#5A00B9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M15.5 21.667C15.5 22.9931 16.0268 24.2648 16.9645 25.2025C17.9021 26.1402 19.1739 26.667 20.5 26.667C21.8261 26.667 23.0979 26.1402 24.0355 25.2025C24.9732 24.2648 25.5 22.9931 25.5 21.667C25.5 20.3409 24.9732 19.0691 24.0355 18.1315C23.0979 17.1938 21.8261 16.667 20.5 16.667C19.1739 16.667 17.9021 17.1938 16.9645 18.1315C16.0268 19.0691 15.5 20.3409 15.5 21.667Z" stroke="#5A00B9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </g>
                                <defs>
                                    <clipPath id="clip0_5_6435">
                                    <rect width="40" height="40" fill="white" transform="translate(0.5)"/>
                                    </clipPath>
                                </defs>
                            </svg>
                        </div>

                        <div class="InvoiceUploader__title">Zrób zdjęcie faktury</div>
                    </div>
                </button>
            </div>

            <PhotoUploader
                v-if="photo"

                :images="files"

                @image:remove="onPhotoRemove"
                @image:add="onPhotoAdd"
                @close="onPhotoClose"
                @accept="onPhotoAccept"
            />

            <div class="InvoiceUploader__preloader">
                <OfferLoader v-if="loading" />
            </div>
        </div>
    `,

    emits: [
        'uploading',
        'uploaded',
        'error',
    ],

    data() {
        return {
            loading: false,
            error: null,

            over: false,
            photo: false,

            files: this.globalfiles ? globalfiles : [],
        };
    },

    props: {
        sme: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },

        label_upload_mobile: { type: String, default: 'Wgraj swoją fakturę' },
        globalfiles: { type: Boolean },
    },

    computed: {
        mobile() {
            return window.innerWidth < 768;
        },
    },

    methods: {
        async onClickUpload() {
            const $ref = $(this.$refs.upload);
            $ref.trigger('click');

            $ref.val('');
        },

        async send() {
            this.$emit('uploading');
            this.error = null;

            const files: UploadFile[] = this.files;

            this.loading = true;

            const MAXSIZE = 30;
            let totalsize = 0;

            const data = new FormData();

            data.append('count', files.length.toString());

            for (const file of files) {
                totalsize += file.file.size;
                data.append('file[]', file.file);
            }

            const xhr = new XMLHttpRequest();

            try {
                if (totalsize > MAXSIZE * 1024 * 1024) {
                    throw new UserError('Zbyt duże pliki. Maksymalny rozmiar plików to 30MB.');
                }

                const r = await $.ajax({
                    xhr: () => xhr,

                    url: this.sme ? '/api/cart/upload/sme' : '/api/cart/upload/b2c',
                    type: 'POST',
                    data: data,

                    contentType: false,
                    processData: false,
                }).promise();

                this.$root.options = r.options;

                if (!r.success) {
                    if (r.error && r.message) {
                        throw new UploadError(r.message, r.soft);
                    }

                    throw new UploadError('Wystąpił błąd', r.soft);
                }

                this.$emit('uploaded', this.$root.options.documents.length);
            } catch (e) {
                if (e instanceof UserError) {
                    this.error = e.message;
                } else {
                    console.error(e);
                }

                this.$emit('error', e);
            }

            this.loading = false;
        },

        async addFiles(files: File[]) {
            for (const file of files) {
                const up: UploadFile = {
                    id: Utils.genUid(),

                    name: file.name,
                    file: file,

                    photo: false,
                };

                this.files.push(up);
            }
        },

        async onChange(e) {
            this.addFiles(e.target.files);
            this.send();
        },

        async onDrop(e: DragEvent) {
            e.preventDefault();

            this.over = false;

            this.addFiles(e.dataTransfer.files);
            this.send();
        },

        onDragEnter(e: DragEvent) {
        },

        onDragOver(e: DragEvent) {
            e.preventDefault();

            this.over = true;
        },

        onDragEnd(e: DragEvent) {
            e.preventDefault();

            this.over = false;
        },

        onManagerAdd() {
            if (this.mobile) {
                this.onClickUpload();
                // this.onPhotoOpen();
            } else {
                this.onClickUpload();
            }
        },

        onPhotoOpen() {
            this.photo = true;
            $('body').addClass('body--lock');
        },

        onPhotoClose() {
            this.photo = false;
            $('body').removeClass('body--lock');

            this.send();
        },

        onPhotoAdd(file: UploadFile) {
            this.files.push(file);
        },

        onPhotoRemove(file: UploadFile) {
            this.files = this.files.filter((v: UploadFile) => v.id != file.id);
            this.send();
        },

        onPhotoRemoveAll() {
            this.files = [];
            this.send();
        },

        onPhotoAccept() {
            this.send();
        },
    },
});
